import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { glassBackdrop, glassModal, glassInput } from './liquidGlass';

interface MatchExtractSheetProps {
  open: boolean;
  onClose: () => void;
  /** Active coach id — used to scope the clip list to the coach's
   *  recent captures. */
  coachId: string | null | undefined;
  /** Called with the picked clip so the parent can (a) seed a default
   *  formation onto the canvas + (b) attach the clip metadata as the
   *  drill's reference clip. */
  onPick: (clip: VideoTagRow) => void;
}

export interface VideoTagRow {
  id: string;
  clip_url: string;
  ts_in: number;
  ts_out: number;
  notes: string | null;
  coach_note: string | null;
  created_at: string;
}

/**
 * Match-extract sheet — lists the coach's recent video tags and lets
 * them seed a drill from one. v1 doesn't extract tracked positions
 * (that pipeline isn't ported yet); it drops a generic formation +
 * attaches the clip as the drill's reference. Coach drags into place.
 */
export function MatchExtractSheet({ open, onClose, coachId, onPick }: MatchExtractSheetProps): React.JSX.Element | null {
  const [clips, setClips] = useState<VideoTagRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (!open || !coachId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      const { data, error: err } = await supabase
        .from('video_tags')
        .select('id, clip_url, ts_in, ts_out, notes, coach_note, created_at')
        .eq('created_by', coachId)
        .order('created_at', { ascending: false })
        .limit(30);
      if (cancelled) return;
      setLoading(false);
      if (err) {
        setError(err.message);
        return;
      }
      setClips((data ?? []) as VideoTagRow[]);
    })();
    return () => { cancelled = true; };
  }, [open, coachId]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return clips;
    return clips.filter((c) =>
      c.clip_url.toLowerCase().includes(q) ||
      (c.notes ?? '').toLowerCase().includes(q) ||
      (c.coach_note ?? '').toLowerCase().includes(q)
    );
  }, [clips, filter]);

  if (!open) return null;

  return (
    <div role="dialog" aria-label="Match extract" style={backdropStyle} onClick={onClose}>
      <div style={sheetStyle} onClick={(e) => e.stopPropagation()}>
        <header style={headerStyle}>
          <div>
            <div style={eyebrowStyle}>Match extract</div>
            <h2 style={titleStyle}>Pick a tagged clip</h2>
          </div>
          <button type="button" onClick={onClose} style={closeBtnStyle} aria-label="Close match extract">✕</button>
        </header>

        <div style={{ padding: '0 16px 12px' }}>
          <input
            type="text"
            placeholder="Filter by URL, note or comment"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={inputStyle}
            autoFocus
          />
        </div>

        <div style={listWrapStyle}>
          {loading && <p style={statusStyle}>Loading recent clips…</p>}
          {error && <p style={{ ...statusStyle, color: '#c0392b' }}>Couldn't load: {error}</p>}
          {!loading && !error && clips.length === 0 && (
            <p style={statusStyle}>No tagged clips yet. Tag a moment on /sessions or /games first.</p>
          )}
          {!loading && filtered.length === 0 && clips.length > 0 && (
            <p style={statusStyle}>No clips match that filter.</p>
          )}
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filtered.map((c) => {
              const duration = c.ts_out - c.ts_in;
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => onPick(c)}
                    style={clipRowStyle}
                    title="Seed a drill from this clip — attaches it as the drill's reference + drops a generic formation"
                  >
                    <span style={tsPill}>{duration}s</span>
                    <span style={{ flex: 1, textAlign: 'left', minWidth: 0 }}>
                      <span style={{ display: 'block', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {c.coach_note || c.notes || 'Untitled clip'}
                      </span>
                      <span style={{ display: 'block', fontSize: 10, opacity: 0.6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {new Date(c.created_at).toLocaleDateString()} · {c.clip_url}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <footer style={footerStyle}>
          <span style={{ flex: 1, fontSize: 11, opacity: 0.7 }}>
            v1 limit: positions aren't extracted from the clip yet — a generic 6v4 lands on the canvas and the clip is attached as the drill's reference.
          </span>
        </footer>
      </div>
    </div>
  );
}

// ---- styles ---------------------------------------------------------------
const backdropStyle: React.CSSProperties = {
  ...glassBackdrop,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
};
const sheetStyle: React.CSSProperties = {
  ...glassModal,
  width: 'min(540px, calc(100vw - 32px))',
  maxHeight: 'calc(100vh - 64px)',
  display: 'flex', flexDirection: 'column',
  fontFamily: 'system-ui, sans-serif',
};
const headerStyle: React.CSSProperties = {
  padding: '14px 16px',
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
};
const eyebrowStyle: React.CSSProperties = {
  fontSize: 10, letterSpacing: 0.5, textTransform: 'uppercase', opacity: 0.55,
};
const titleStyle: React.CSSProperties = { margin: 0, fontSize: 18, fontWeight: 600 };
const closeBtnStyle: React.CSSProperties = {
  background: 'transparent', color: '#fff',
  border: '1px solid rgba(255,255,255,0.2)', borderRadius: 6,
  width: 32, height: 32, cursor: 'pointer',
};
const inputStyle: React.CSSProperties = glassInput;
const listWrapStyle: React.CSSProperties = {
  flex: 1, overflowY: 'auto', padding: '0 16px 16px',
};
const statusStyle: React.CSSProperties = {
  textAlign: 'center', opacity: 0.7, padding: 24, fontSize: 13,
};
const clipRowStyle: React.CSSProperties = {
  display: 'flex', gap: 12, alignItems: 'center',
  width: '100%', padding: '8px 10px',
  background: 'rgba(255,255,255,0.04)', color: '#fff',
  border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6,
  cursor: 'pointer', textAlign: 'left',
  fontFamily: 'inherit',
};
const tsPill: React.CSSProperties = {
  background: '#2980B9', color: '#fff',
  padding: '4px 8px', borderRadius: 999,
  fontSize: 11, fontWeight: 700, flexShrink: 0,
};
const footerStyle: React.CSSProperties = {
  padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.1)',
};
